import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, InputTextArea, SparkIcon } from '@spark';
import GenAiModalShell from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/GenAiModalShell';
import FieldLabel from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/FieldLabel';
import TextAreaField from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/TextAreaField';
import ExampleLabelsField from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/ExampleLabelsField';
import DefineTaskStep from '@/features/datasets/components/DataCollection/GenerateDataWithAI/steps/DefineTaskStep';
import RecordsConfigStep from '@/features/datasets/components/DataCollection/GenerateDataWithAI/steps/RecordsConfigStep';
import ReviewStep from '@/features/datasets/components/DataCollection/GenerateDataWithAI/steps/ReviewStep';
import { createDefaultSettings } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/AdvancedSettingsSection';
import useDatasetNameField from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/useDatasetNameField';
import {
    generationSessionConfig,
    multiLabelExampleRowsFromConfig,
    settingsFromConfig,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/generationSessionPrefill';
import useGenerationSessions from '@/features/datasets/components/DataCollection/GenerateDataWithAI/useGenerationSessions';
import useGenerationTargets, {
    catalogLabelNames,
    NO_PREDICTIONS_GENERATION_TARGET,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/useGenerationTargets';
import useRecordsState from '@/features/datasets/components/DataCollection/GenerateDataWithAI/useRecordsState';
import { formatRecordsBanner } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/flows/ClassificationFlow';
import { HELP_TEXTS } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/constants';
import { reportRequiredFieldsError } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/requiredFieldsError';
import { fetchGenerationDatasetNameSuggestion } from '@/features/datasets/utils/aiGenerationDataset';
import { targetNamesFromConfig } from '@/features/datasets/utils/datasetSourceConfig';
import { retrievalTaskTypeLabel, sessionOptionTitle } from '@/features/datasets/utils/retrievalAgentDataset';

let exampleRowSeq = 0;
const newExampleRow = () => ({ id: `ex_${(exampleRowSeq += 1)}`, text: '', labels: [] });

/** Empty label selection is No predictions (none of the real classes). */
export const toWireMultiLabelExamples = (exampleRows) =>
    (exampleRows || []).flatMap((row) => {
        const text = (row.text || '').trim();
        if (!text) return [];
        const labels = (row.labels || []).filter(Boolean);
        return [
            {
                kind: 'multi',
                text,
                response: {
                    kind: 'multi',
                    labels: labels.length ? labels : [NO_PREDICTIONS_GENERATION_TARGET],
                },
            },
        ];
    });

/**
 * Multi-label classification flow (Figma ml frames): Define Task with Active
 * Labels, Examples+Labels rows, Exclude, and Advanced settings → Records to
 * generate + per-label description (advanced above it) → Review & Generate.
 */
const MultiLabelFlow = ({ open, onClose, onGenerate, usecase, warningBanner, disabled = false }) => {
    const [step, setStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [goal, setGoal] = useState('');
    const [description, setDescription] = useState('');
    const [goalError, setGoalError] = useState(false);
    const [descriptionError, setDescriptionError] = useState(false);
    const [exampleRows, setExampleRows] = useState([newExampleRow()]);
    const [exclude, setExclude] = useState('');
    const [settings, setSettings] = useState(createDefaultSettings());
    const [footerError, setFooterError] = useState('');
    const [loadedSessionTitle, setLoadedSessionTitle] = useState(undefined);

    const sessions = useGenerationSessions(usecase?._id, open);
    const { datasetName, onDatasetNameChange, datasetNameError, validateDatasetName } = useDatasetNameField({
        open,
        usecaseId: usecase?._id,
        fetchSuggestion: fetchGenerationDatasetNameSuggestion,
    });

    const { targets } = useGenerationTargets(usecase, open);
    const targetNames = useMemo(() => targets.map((target) => target.name), [targets]);
    // null until the user edits the Active Labels; falls back to the usecase's labels.
    const [editedNames, setEditedNames] = useState(null);
    const activeNames = editedNames ?? targetNames;
    const activeLabelOptions = useMemo(
        () => [...new Set([...targetNames, ...activeNames])],
        [targetNames, activeNames]
    );
    const labelOptions = useMemo(() => catalogLabelNames(activeNames), [activeNames]);
    const records = useRecordsState(activeNames);

    const reset = () => {
        setStep(0);
        setGoal('');
        setDescription('');
        setGoalError(false);
        setDescriptionError(false);
        setExampleRows([newExampleRow()]);
        setExclude('');
        setSettings(createDefaultSettings());
        setEditedNames(null);
        setFooterError('');
        setLoadedSessionTitle(undefined);
    };

    /** Prefill the wizard from a past generation configuration (Dataset Name keeps its fresh default). */
    const applySession = (session) => {
        const config = generationSessionConfig(session);
        if (!config) return;
        setLoadedSessionTitle(sessionOptionTitle(session));
        setGoal(config.goal);
        setGoalError(false);
        setDescription(config.description);
        setDescriptionError(false);
        setSettings(settingsFromConfig(config));
        const rows = multiLabelExampleRowsFromConfig(config);
        setExampleRows(rows.length ? rows : [newExampleRow()]);
        setExclude(config.settings.exclude || '');
        const names = targetNamesFromConfig(config);
        if (names.length) setEditedNames(names);
        records.prefill({
            names: names.length ? names : activeNames,
            totalRecords: config.datasetSize,
            counts: config.recordCounts,
            configs: config.targetConfigs,
        });
        setFooterError('');
    };

    /** Deselecting a label also unassigns it from the Define Task example rows. */
    const handleLabelNamesChange = (next) => {
        const removed = activeNames.filter((name) => !next.includes(name));
        setEditedNames(next);
        setFooterError('');
        if (!removed.length) return;
        setExampleRows((prev) =>
            prev.map((row) => ({
                ...row,
                labels: (row.labels || []).filter((name) => !removed.includes(name)),
            }))
        );
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const validateStep0 = () => {
        const nextGoalError = !goal.trim();
        const nextDescriptionError = !description.trim();
        const datasetNameValid = validateDatasetName();
        setGoalError(nextGoalError);
        setDescriptionError(nextDescriptionError);
        return !nextGoalError && !nextDescriptionError && datasetNameValid;
    };

    const patchRow = (id, key, value) => {
        setExampleRows((prev) => prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)));
    };

    const buildExamples = () => toWireMultiLabelExamples(exampleRows);

    const handlePrimary = async () => {
        if (step === 0) {
            if (validateStep0()) {
                setFooterError('');
                setStep(1);
            } else {
                setFooterError(reportRequiredFieldsError());
            }
            return;
        }
        if (step === 1) {
            if (!records.totalRecordsError) {
                setFooterError('');
                setStep(2);
            } else {
                setFooterError(reportRequiredFieldsError());
            }
            return;
        }
        try {
            setIsSubmitting(true);
            await onGenerate({
                datasetName: datasetName.trim(),
                goal: goal.trim(),
                description: description.trim(),
                examples: buildExamples(),
                exclude: exclude.trim(),
                totalRecords: records.totalRecords,
                recordCounts: records.counts,
                targetConfigs: records.configs,
                settings,
            });
            handleClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <GenAiModalShell
            open={open}
            onClose={handleClose}
            title="Generate data with AI"
            subtitle="Define what the dataset should teach, then configure the labels and examples that will guide generation."
            primaryLabel={step === 2 ? 'Generate' : 'Next'}
            onPrimary={handlePrimary}
            primaryDisabled={disabled}
            primaryDisableWhenReadOnly={step === 2}
            isSubmitting={isSubmitting}
            showBack={step > 0}
            onBack={() => {
                setFooterError('');
                setStep(step - 1);
            }}
            footerError={footerError}
        >
            {warningBanner}
            {step === 0 ? (
                <DefineTaskStep
                    bannerText={formatRecordsBanner(targets)}
                    datasetName={datasetName}
                    onDatasetNameChange={onDatasetNameChange}
                    datasetNameError={datasetNameError}
                    sessions={sessions}
                    loadedSessionTitle={loadedSessionTitle}
                    onLoadSession={applySession}
                    goal={goal}
                    description={description}
                    goalError={goalError}
                    descriptionError={descriptionError}
                    onGoalChange={(value) => {
                        setGoal(value);
                        setGoalError(false);
                        setFooterError('');
                    }}
                    onDescriptionChange={(value) => {
                        setDescription(value);
                        setDescriptionError(false);
                        setFooterError('');
                    }}
                    labelHeading="Active Labels"
                    labelOptions={activeLabelOptions}
                    labelNames={activeNames}
                    onLabelNamesChange={handleLabelNamesChange}
                    settings={settings}
                    onSettingsChange={setSettings}
                    showTextLengthOptionHelp
                >
                    <div className="gdai-section">
                        {exampleRows.map((row, index) => (
                            <div key={row.id} className="gdai-example-row">
                                <div className="gdai-field gdai-example-row__text">
                                    <FieldLabel text="Examples" help={HELP_TEXTS.examples} />
                                    <InputTextArea
                                        className="gdai-textarea gdai-example-textarea"
                                        showLabel={false}
                                        size="medium"
                                        rows={1}
                                        placeholder={'e.g., "This is a great beautiful movie"'}
                                        value={row.text}
                                        onChange={(event) => patchRow(row.id, 'text', event.target.value)}
                                    />
                                </div>
                                <ExampleLabelsField
                                    options={labelOptions}
                                    value={row.labels}
                                    onChange={(labels) => patchRow(row.id, 'labels', labels)}
                                />
                                {exampleRows.length > 1 ? (
                                    <button
                                        type="button"
                                        className="gdai-example-row__remove"
                                        aria-label={`Remove example ${index + 1}`}
                                        onClick={() =>
                                            setExampleRows((prev) => prev.filter((item) => item.id !== row.id))
                                        }
                                    >
                                        <SparkIcon slug="trash" size="small" />
                                    </button>
                                ) : null}
                            </div>
                        ))}
                        <div>
                            <Button
                                variant="secondary"
                                size="medium"
                                startIcon={<SparkIcon slug="plus" size="medium" />}
                                onClick={() => setExampleRows((prev) => [...prev, newExampleRow()])}
                            >
                                Add Example
                            </Button>
                        </div>
                        <TextAreaField
                            label="Exclude"
                            help={HELP_TEXTS.exclude}
                            placeholder="e.g., Do not generate ambiguous or mixed-sentiment samples"
                            value={exclude}
                            onChange={setExclude}
                            height={94}
                        />
                    </div>
                </DefineTaskStep>
            ) : null}
            {step === 1 ? (
                <RecordsConfigStep
                    variant="multiLabel"
                    targetNames={activeNames}
                    totalRecords={records.totalRecords}
                    onTotalRecordsChange={records.onTotalRecordsChange}
                    counts={records.counts}
                    onCountChange={records.onCountChange}
                    configs={records.configs}
                    onConfigChange={records.onConfigChange}
                    totalRecordsError={records.totalRecordsError}
                />
            ) : null}
            {step === 2 ? (
                <ReviewStep
                    goal={goal.trim()}
                    customGuidelines={description.trim()}
                    languages={settings.languages}
                    taskTypeLabel={retrievalTaskTypeLabel(usecase)}
                    totalRecords={records.totalRecords}
                />
            ) : null}
        </GenAiModalShell>
    );
};

MultiLabelFlow.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onGenerate: PropTypes.func.isRequired,
    usecase: PropTypes.object,
    warningBanner: PropTypes.node,
    disabled: PropTypes.bool,
};

export default MultiLabelFlow;
